// Common theme extensions for the application
// These are style objects that can be applied to MUI components via the sx prop or used in component styles

// 45° corner cut, the brand's replacement for border radius
const cutPolygon = (size) =>
  `${size}px 0, 100% 0, 100% calc(100% - ${size}px), calc(100% - ${size}px) 100%, 0 100%, 0 ${size}px`

// The same polygon one hairline further in; a 45° edge moves by √2 per px of inset
const innerCutPolygon = (size) => {
  const k = (size + 0.41).toFixed(2)
  return `${k}px 1px, calc(100% - 1px) 1px, calc(100% - 1px) calc(100% - ${k}px), calc(100% - ${k}px) calc(100% - 1px), 1px calc(100% - 1px), 1px ${k}px`
}

/**
 * 45° corner cut, the brand's replacement for border radius. With `outline`
 * a 1px ring in that colour follows the cut edges (a plain border would be
 * clipped off the diagonals); recolour it on hover via `'&:hover::after'`.
 */
export const cutCorners = (size = 14, outline = null) => ({
  borderRadius: 0,
  clipPath: `polygon(${cutPolygon(size)})`,
  ...(outline && {
    position: 'relative',
    border: 'none',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      zIndex: 1,
      pointerEvents: 'none',
      background: outline,
      clipPath: `polygon(evenodd, ${cutPolygon(size)}, ${innerCutPolygon(size)})`,
    },
  }),
})

// Surface backgrounds - theme-aware. Both modes are flat: panels on a chassis.
export const getGradients = (mode = 'dark') => {
  if (mode === 'light') {
    return {
      sidebar: '#ffffff',
      card: '#ffffff',
      cardSubtle: '#ffffff',
      metricCard: '#ffffff',
      header: '#ffffff',
      background: '#f3f5fc',
    }
  }
  return {
    sidebar: '#07090a',
    card: '#121a1d',
    cardSubtle: '#121a1d',
    metricCard: '#121a1d',
    header: '#121a1d',
    background: '#07090a',
  }
}

// Card styles - theme-aware: a panel with a hairline and cut corners, no shadow
export const getCardStyles = (theme) => ({
  root: {
    ...cutCorners(14, theme.palette.divider),
    background: theme.palette.background.paper,
    height: '100%',
  },
  content: {
    padding: 3,
  },
  title: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: 2,
  },
})

// Uppercase, letter-spaced metadata label
export const getEyebrowStyles = (theme) => ({
  ...theme.typography.overline,
  color: theme.palette.text.secondary,
})

// Layout styles
export const layoutStyles = {
  flexBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  flexCenter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  fullWidth: {
    width: '100%',
  },
  fullHeight: {
    height: '100%',
  },
  page: {
    padding: { xs: 2, md: 3 },
    width: '100%',
    minHeight: '100vh',
  },
  responsiveContainer: {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'hidden',
    wordBreak: 'break-word',
    '& h1, & h2, & h3, & h4, & h5, & h6, & p, & span': {
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& .MuiTable-root': {
      tableLayout: 'fixed',
      width: '100%',
    },
    '& .MuiCard-root': {
      maxWidth: '100%',
    },
  },
}

// Navigation styles - theme-aware
export const getNavigationStyles = (theme) => ({
  listItem: {
    borderRadius: 0,
    marginBottom: 0.5,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.accentTint.subtle,
      boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
      '&:hover': {
        backgroundColor: theme.palette.accentTint.strong,
      },
    },
  },
  listItemText: {
    '& .MuiTypography-root': {
      color: theme.palette.text.primary,
      fontWeight: 500,
    },
  },
  listItemIcon: {
    color: theme.palette.text.primary,
    minWidth: 36,
  },
  // Square status pip: accent when live
  livePip: {
    width: 6,
    height: 6,
    borderRadius: 0,
    flexShrink: 0,
    backgroundColor: theme.palette.primary.main,
  },
})

// Scrollbar styles
export const scrollbarStyles = {
  thin: {
    '&::-webkit-scrollbar': {
      width: '8px',
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255,255,255,0.2)',
      '&:hover': {
        background: 'rgba(255,255,255,0.3)',
      },
    },
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255,255,255,0.2) transparent',
  },
}

// Theme-aware variant: hairline thumb in both modes
export const getScrollbarStyles = (theme) => ({
  thin: {
    '&::-webkit-scrollbar': {
      width: '8px',
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.divider,
      '&:hover': {
        background: theme.palette.text.disabled,
      },
    },
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.divider} transparent`,
  },
})

// Component-specific color extensions for theme
// These can be accessed via theme.palette.status, theme.palette.chart, etc.
export const componentColors = {
  status: {
    online: '#0ddfcb',
    offline: '#788990',
    warning: '#ed6c02',
    error: '#ef4e5c',
    info: '#788990',
  },
  video: {
    live: '#ef4e5c',
    playbackSpeed: '#ed6c02',
    playing: '#0ddfcb',
  },
}
